import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAllPartenaires } from "../../services/partenairesServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const FALLBACK = "/img/no-image.jpg";

// ✅ /uploads/... => absolu API (Vite + CRA), sinon laisse passer https://, /img/...
function toAbsolute(u) {
  if (!u) return "";
  const s = String(u).trim();

  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;

  if (!s.startsWith("/uploads/") && !s.startsWith("/uploads")) return s;

  const rawBase =
    (typeof import.meta !== "undefined" &&
      import.meta.env &&
      import.meta.env.VITE_API_URL) ||
    process.env.REACT_APP_API_URL ||
    "";

  const base = String(rawBase).replace(/\/$/, "");
  return base ? `${base}${s}` : s;
}

const PartenaireDetail = () => {
  useScrollToTop();
  const navigate = useNavigate();
  const { nom } = useParams();

  const [partenaire, setPartenaire] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mapUrl, setMapUrl] = useState("");

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getAllPartenaires();
        const list = Array.isArray(data) ? data : [];
        const decodedNom = decodeURIComponent(nom || "");

        const match = list.find((p) => p?.nom === decodedNom);
        setPartenaire(match || null);
        setNotFound(!match);
      } catch (e) {
        console.error("Erreur getAllPartenaires:", e);
        setPartenaire(null);
        setNotFound(true);
      }
    };
    fetch();
  }, [nom]);

  useEffect(() => {
    if (partenaire?.adresse) {
      const encoded = encodeURIComponent(partenaire.adresse);
      setMapUrl(`https://www.google.com/maps?q=${encoded}&output=embed`);
    } else {
      setMapUrl("");
    }
  }, [partenaire]);

  const imgLogo = useMemo(
    () => toAbsolute(partenaire?.photo_url) || FALLBACK,
    [partenaire?.photo_url],
  );
  const img2 = useMemo(
    () => toAbsolute(partenaire?.photo2_url),
    [partenaire?.photo2_url],
  );
  const img3 = useMemo(
    () => toAbsolute(partenaire?.photo3_url),
    [partenaire?.photo3_url],
  );

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

  if (!partenaire && !notFound)
    return <p className="text-center mt-20">Chargement...</p>;

  if (notFound)
    return (
      <div className="max-w-3xl mx-auto mt-[120px] p-6">
        <button
          onClick={() => navigate("/partenaires")}
          className="text-sm px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition"
        >
          ← Retour à la liste des partenaires
        </button>
        <p className="mt-6 text-slate-700 dark:text-slate-200">
          Partenaire introuvable.
        </p>
      </div>
    );

  return (
    <div className="flex flex-col w-full max-w-[1280px] mx-auto p-6 px-10 mt-[120px] bg-backgroundMain dark:bg-backgroundMainDark dark:text-white">
      <div className="mb-4">
        <button
          onClick={() => navigate("/partenaires")}
          className="text-sm px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition"
        >
          ← Retour à la liste des partenaires
        </button>
      </div>

      {/* Titre + logo */}
      <div className="flex flex-col md:flex-row items-center md:items-start mb-8 gap-6">
        <img
          src={imgLogo}
          alt={partenaire.nom}
          onError={onImgError}
          className="w-32 h-32 object-contain"
        />
        <h1 className="text-3xl font-bold text-center md:text-left">
          {partenaire.nom}
        </h1>
      </div>

      {/* Bloc 1 : photo2 à gauche, description2 à droite */}
      {(img2 || partenaire.description2) && (
        <div className="flex flex-col md:flex-row gap-6 mb-8 items-center">
          {img2 && (
            <img
              src={img2}
              alt="photo 2"
              onError={onImgError}
              className="w-full md:w-1/2 rounded shadow object-contain bg-white/60 dark:bg-slate-900/40"
            />
          )}
          {partenaire.description2 && (
            <div
              className="w-full md:w-1/2 prose dark:prose-invert max-w-none text-justify"
              dangerouslySetInnerHTML={{ __html: partenaire.description2 }}
            />
          )}
        </div>
      )}

      {/* Bloc 2 : description3 à gauche, photo3 à droite */}
      {(partenaire.description3 || img3) && (
        <div className="flex flex-col md:flex-row gap-6 mb-8 items-center md:flex-row-reverse">
          {img3 && (
            <img
              src={img3}
              alt="photo 3"
              onError={onImgError}
              className="w-full md:w-1/2 rounded shadow object-contain bg-white/60 dark:bg-slate-900/40"
            />
          )}
          {partenaire.description3 && (
            <div
              className="w-full md:w-1/2 prose dark:prose-invert max-w-none text-justify"
              dangerouslySetInnerHTML={{ __html: partenaire.description3 }}
            />
          )}
        </div>
      )}

      {/* Coordonnées */}
      <div className="text-sm text-gray-700 dark:text-gray-300 space-y-2 mb-8">
        {partenaire.adresse && (
          <p>
            <strong>Adresse :</strong> {partenaire.adresse}
          </p>
        )}
        {partenaire.tel && (
          <p>
            <strong>Téléphone :</strong> {partenaire.tel}
          </p>
        )}
        {partenaire.Mail && (
          <p>
            <strong>Email :</strong>{" "}
            <a
              href={`mailto:${partenaire.Mail}`}
              className="text-blue-600 underline"
            >
              {partenaire.Mail}
            </a>
          </p>
        )}
        {partenaire.link_url && (
          <p>
            <strong>Site :</strong>{" "}
            <a
              href={partenaire.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline break-all"
            >
              {partenaire.link_url}
            </a>
          </p>
        )}
      </div>

      {/* Carte Google Maps */}
      {mapUrl && (
        <div className="w-full h-64 rounded overflow-hidden shadow mb-4">
          <iframe
            title="Google Maps"
            src={mapUrl}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
};

export default PartenaireDetail;
